import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import PropTypes from 'prop-types';
import SoftTypography from 'components/SoftTypography';
import SoftBox from 'components/SoftBox';
import Grid from '@mui/material/Grid';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftSelect from 'components/SoftSelect';
import SoftButton from 'components/SoftButton';
import ClientSelector from 'components/ClientSelector';
import axios from 'axios';
import Swal from 'sweetalert2';

function ModalCobranza({ show, handleClose, getData, documentToEdit = null }) {
  const [formData, setFormData] = useState({
    // Aquí se guarda el id del cliente (número o string) para que coincida con el valor en ClientSelector
    cliente: '',
    rut: '',
    razonSocial: '',
    ciudad: '',
    comuna: '',
    direccion: '',
    tipoCambio: '',
    numeroOrden: '',
    operacion: '',
    blGuiaAerea: '',
    flete: '',
    condiciones: '',
    tipoMoneda: '',
    costo: '',
    telefono: '',
    nota1: '',
    nota2: '',
    nota3: '',
  });

  const [currencyTypeOptions, setCurrencyTypeOptions] = useState([]);
  const [isLoadingCurrencyTypes, setIsLoadingCurrencyTypes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Al abrir el modal, si hay documento para editar se obtienen los datos
  useEffect(() => {
    if (!show) return;

    const fetchDocumentData = async (id) => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/finance/invoice/${id}/`);
        const data = response.data;
        console.log(data)
        // Se asume que data.cliente es el id del cliente
        setFormData({
          cliente: data.cliente, 
          rut: data.rut,
          razonSocial: data.razonSocial,
          ciudad: data.ciudad,
          comuna: data.comuna,
          direccion: data.direccion,
          tipoCambio: data.tipoCambio,
          numeroOrden: data.numeroOrden,
          operacion: data.operacion,
          blGuiaAerea: data.blGuiaAerea,
          flete: data.flete,
          condiciones: data.condiciones,
          tipoMoneda: data.tipoMoneda,
          costo: data.costo,
          telefono: data.telefono,
          nota1: data.nota1,
          nota2: data.nota2,
          nota3: data.nota3,
        });
      } catch (error) {
        console.error('Error fetching document data:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los datos del documento.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    };

    if (documentToEdit) {
      fetchDocumentData(documentToEdit.id);
    } else {
      // En modo creación se resetea el formulario
      setFormData({
        cliente: '',
        rut: '',
        razonSocial: '',
        ciudad: '',
        comuna: '',
        direccion: '',
        tipoCambio: '',
        numeroOrden: '',
        operacion: '',
        blGuiaAerea: '',
        flete: '',
        condiciones: '',
        tipoMoneda: '',
        costo: '',
        telefono: '',
        nota1: '',
        nota2: '',
        nota3: '',
      });
    }
  }, [show, documentToEdit]);

  // Al seleccionar un cliente desde ClientSelector se actualiza el formulario
  const handleClientSelection = selectedOption => {
    if (!selectedOption) return;
    const { comuna, direccion, nombre, razon_social, rut, telefono, value } = selectedOption;
    setFormData(prev => ({
      ...prev,
      telefono,
      // Se guarda el id del cliente
      cliente: nombre,
      rut,
      ciudad: 'N/A',
      comuna,
      direccion,
      razonSocial: razon_social,
    }));
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = selectedOption => {
    if (!selectedOption) return;
    setFormData(prev => ({
      ...prev,
      tipoMoneda: selectedOption.value,
    }));
  };

  const fetchCurrencyTypes = async () => {
    setIsLoadingCurrencyTypes(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/finance/currency-types/`
      );
      if (response.data?.results) {
        const options = response.data.results.map(currency => ({
          value: currency.name,
          label: currency.name,
        }));
        setCurrencyTypeOptions(options);
      }
    } catch (error) {
      console.error('Error fetching currency types:', error);
      setCurrencyTypeOptions([]);
    } finally {
      setIsLoadingCurrencyTypes(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchCurrencyTypes();
  }, [show]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let response;
      if (documentToEdit) {
        response = await axios.put(
          `${process.env.REACT_APP_SERVER_URL}/api/finance/invoice/${documentToEdit.id}/`,
          formData
        );
        Swal.fire({
          title: 'Documento Actualizado',
          text: 'El documento se ha actualizado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/finance/invoice-list/`,
          formData
        );
        Swal.fire({
          title: 'Cobranza Generada',
          text: 'La cobranza se ha generado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
    }
    
    getData();
    handleClose();
} catch (error) {
    console.log(formData)
    Swal.fire({
        title: 'Error',
        text: 'Hubo un error al procesar la operación',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      console.error('Error:', error.response?.data || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      backdropClassName="modal-backdrop"
      className="modal-container"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {documentToEdit ? 'Editar Documento' : 'Nueva Cobranza'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              {/* Se deja que ClientSelector haga la llamada; 
                  se pasa el id del cliente almacenado en formData */}
              <ClientSelector 
                baseOptions={null}
                selectedClient={formData.cliente} 
                setSelectedClient={handleClientSelection} 
              />
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Ciudad"
                    name="ciudad"
                    value={formData.ciudad}
                    placeholder="Ciudad"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Comuna"
                    name="comuna"
                    value={formData.comuna}
                    placeholder="Comuna"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Dirección"
                    name="direccion"
                    value={formData.direccion}
                    placeholder="Dirección"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Condiciones"
                    name="condiciones"
                    value={formData.condiciones}
                    placeholder="Condiciones"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="R.U.T."
                    name="rut"
                    value={formData.rut}
                    placeholder="R.U.T."
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="number"
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    placeholder="Teléfono"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="number"
                    label="N° Orden"
                    name="numeroOrden"
                    value={formData.numeroOrden}
                    placeholder="N° Orden"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Operación"
                    name="operacion"
                    value={formData.operacion}
                    placeholder="Operación"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Flete Aéreo/Marítimo
                  </SoftTypography>
                  <FormField
                    type="text"
                    name="flete"
                    value={formData.flete}
                    placeholder="Flete Aéreo/Marítimo"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Razón Social"
                    name="razonSocial"
                    value={formData.razonSocial}
                    placeholder="Razón Social"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="number"
                    label="Tipo Cambio"
                    name="tipoCambio"
                    value={formData.tipoCambio}
                    placeholder="Tipo Cambio"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    BL / Guía Aérea
                  </SoftTypography>
                  <FormField
                    type="text"
                    name="blGuiaAerea"
                    value={formData.blGuiaAerea}
                    placeholder="BL / Guía Aérea"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Tipo Moneda
                  </SoftTypography>
                  <SoftSelect
                    options={currencyTypeOptions}
                    placeholder="Seleccionar..."
                    value={currencyTypeOptions.find(option => option.value === formData.tipoMoneda)}
                    onChange={handleSelectChange}
                    isLoading={isLoadingCurrencyTypes}
                    isClearable
                  />
                </SoftBox>
              </SoftBox>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="number"
                    label="Costo"
                    name="costo"
                    value={formData.costo}
                    placeholder="Costo"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Nota 1"
                    name="nota1"
                    value={formData.nota1}
                    placeholder="Nota 1"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Nota 2"
                    name="nota2"
                    value={formData.nota2}
                    placeholder="Nota 2"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftBox>
                  <FormField
                    type="text"
                    label="Nota 3"
                    name="nota3"
                    value={formData.nota3}
                    placeholder="Nota 3"
                    onChange={handleInputChange}
                  />
                </SoftBox>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </Modal.Body>

      <Modal.Footer>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleSubmit}
          sx={{ mr: 2 }}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? 'Procesando...'
            : documentToEdit
            ? 'Actualizar'
            : 'Generar'}
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="error"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

ModalCobranza.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  getData: PropTypes.func.isRequired,
  documentToEdit: PropTypes.object,
};

export default ModalCobranza;
